import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Upload, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin, 
  Users, 
  Star,
  Clock,
  Edit3,
  X
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../../config/api';

const EditVolunteerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [volunteer, setVolunteer] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState('');

  // Helper function to construct profile picture URL
  const getProfilePictureUrl = (filePath) => {
    if (!filePath) return '/images/r.png';
    if (filePath.startsWith('http')) return filePath;
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3011'}/${filePath}`;
  };

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    },
    skills: [''],
    interests: [''],
    availability: {
      weekdays: false,
      weekends: false,
      evenings: false,
      flexible: false
    },
    experience: '',
    motivation: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchVolunteer();
  }, [id]);

  // Debug: Log form data changes
  useEffect(() => {
    console.log('Form data updated:', formData);
  }, [formData]);

  const fetchVolunteer = async () => {
    try {
      setLoading(true);
      console.log('Fetching volunteer with ID:', id);
      console.log('API URL:', `/volunteers/${id}`);
      
      // Check authentication
      const ngoToken = localStorage.getItem('ngoToken');
      const ngoData = localStorage.getItem('ngoData');
      console.log('NGO Token exists:', !!ngoToken);
      console.log('NGO Data exists:', !!ngoData);
      
      const response = await api.get(`/volunteers/${id}`);
      console.log('API Response:', response);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      // The API returns { success: true, data: volunteer }
      const volunteerData = response.data?.data || response.data;
      console.log('Volunteer Data:', volunteerData);
      console.log('Volunteer Data type:', typeof volunteerData);
      console.log('Volunteer Data keys:', volunteerData ? Object.keys(volunteerData) : 'No data');
      
      if (!volunteerData) {
        throw new Error('No volunteer data received');
      }
      
      setVolunteer(volunteerData);
      
      // Ensure we have proper fallback values for all fields
      const formDataToSet = {
        firstName: volunteerData.firstName || '',
        lastName: volunteerData.lastName || '',
        email: volunteerData.email || '',
        phone: volunteerData.phone || '',
        dateOfBirth: volunteerData.dateOfBirth ? new Date(volunteerData.dateOfBirth).toISOString().split('T')[0] : '',
        gender: volunteerData.gender || '',
        address: volunteerData.address || '',
        city: volunteerData.city || '',
        state: volunteerData.state || '',
        pincode: volunteerData.pincode || '',
        emergencyContact: {
          name: volunteerData.emergencyContact?.name || '',
          relationship: volunteerData.emergencyContact?.relationship || '',
          phone: volunteerData.emergencyContact?.phone || ''
        },
        skills: Array.isArray(volunteerData.skills) && volunteerData.skills.length > 0 ? volunteerData.skills : [''],
        interests: Array.isArray(volunteerData.interests) && volunteerData.interests.length > 0 ? volunteerData.interests : [''],
        availability: {
          weekdays: volunteerData.availability?.weekdays || false,
          weekends: volunteerData.availability?.weekends || false,
          evenings: volunteerData.availability?.evenings || false,
          flexible: volunteerData.availability?.flexible || false
        },
        experience: volunteerData.experience || '',
        motivation: volunteerData.motivation || '',
        status: volunteerData.status || 'pending'
      };
      
      console.log('Setting form data:', formDataToSet);
      setFormData(formDataToSet);

      if (volunteerData.profilePicture) {
        console.log('Setting profile picture:', volunteerData.profilePicture);
        setPreviewImage(getProfilePictureUrl(volunteerData.profilePicture));
      } else {
        console.log('No profile picture found, using default');
        setPreviewImage('/images/r.png');
      }
    } catch (error) {
      console.error('Error fetching volunteer:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
        navigate('/ngo/login');
      } else if (error.response?.status === 404) {
        toast.error('Volunteer not found');
        navigate('/ngo/dashboard/volunteers');
      } else {
        toast.error('Failed to load volunteer details');
        navigate('/ngo/dashboard/volunteers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmergencyContactChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value
      }
    }));
  };

  const handleAvailabilityChange = (field) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [field]: !prev.availability[field]
      }
    }));
  };

  const handleArrayFieldChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayFieldItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayFieldItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      setProfileImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    setPreviewImage(getProfilePictureUrl(volunteer?.profilePicture));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const submitData = new FormData();
      
      // Basic fields
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('dateOfBirth', formData.dateOfBirth);
      submitData.append('gender', formData.gender);
      submitData.append('address', formData.address);
      submitData.append('city', formData.city);
      submitData.append('state', formData.state);
      submitData.append('pincode', formData.pincode);
      
      // Complex fields as JSON strings
      submitData.append('emergencyContact', JSON.stringify(formData.emergencyContact));
      submitData.append('skills', JSON.stringify(formData.skills.filter(skill => skill.trim())));
      submitData.append('interests', JSON.stringify(formData.interests.filter(interest => interest.trim())));
      submitData.append('availability', JSON.stringify(formData.availability));
      submitData.append('experience', formData.experience);
      submitData.append('motivation', formData.motivation);
      submitData.append('status', formData.status);
      
      // Profile picture if changed
      if (profileImage) {
        submitData.append('profilePicture', profileImage);
      }
      
      console.log('Submitting volunteer update:', Object.fromEntries(submitData));
      
      const response = await api.put(`/volunteers/${id}`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Update response:', response);
      toast.success('Volunteer updated successfully!');
      navigate('/ngo/dashboard/volunteers/manage');
      
    } catch (error) {
      console.error('Error updating volunteer:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Validation error. Please check your input.');
      } else if (error.response?.status === 404) {
        toast.error('Volunteer not found');
      } else {
        toast.error('Failed to update volunteer. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Loading volunteer details...</p>
      </div>
    );
  }

  if (!volunteer) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Volunteer not found</h3>
        <button
          onClick={() => navigate('/ngo/dashboard/volunteers')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Volunteers
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
             {/* Header */}
       <div className="flex items-center justify-between mb-6">
         <div className="flex items-center space-x-4">
           <button
             onClick={() => navigate('/ngo/dashboard/volunteers/manage')}
             className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
           >
             <ArrowLeft className="w-5 h-5 mr-2" />
             Back to Volunteers
           </button>
           <div>
             <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Edit Volunteer</h1>
             <p className="text-sm text-gray-600 dark:text-gray-400">
               Update volunteer information and details
             </p>
           </div>
         </div>
         
         <div className="flex items-center space-x-2">
           <span className={`px-3 py-1 rounded-full text-xs font-medium ${
             volunteer.status === 'active' ? 'bg-green-100 text-green-800' :
             volunteer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
             'bg-gray-100 text-gray-800'
           }`}>
             {volunteer.status}
           </span>
           <span className="text-sm text-gray-500">
             ID: {volunteer._id?.slice(-8)}
           </span>
         </div>
       </div>

               {/* Debug Info - Remove this after testing */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-yellow-800 mb-2">Debug Info (Current Data)</h4>
          <div className="text-sm text-yellow-700 space-y-1">
            <div><strong>Name:</strong> {volunteer.firstName || 'None'} {volunteer.lastName || 'None'}</div>
            <div><strong>Email:</strong> {volunteer.email || 'None'}</div>
            <div><strong>Phone:</strong> {volunteer.phone || 'None'}</div>
            <div><strong>Profile Picture:</strong> {volunteer.profilePicture ? getProfilePictureUrl(volunteer.profilePicture) : 'None'}</div>
            <div><strong>Skills:</strong> {Array.isArray(volunteer.skills) ? volunteer.skills.join(', ') : 'None'}</div>
            <div><strong>Interests:</strong> {Array.isArray(volunteer.interests) ? volunteer.interests.join(', ') : 'None'}</div>
            <div><strong>Experience:</strong> {volunteer.experience || 'None'}</div>
            <div><strong>Motivation:</strong> {volunteer.motivation || 'None'}</div>
            <div><strong>Address:</strong> {volunteer.address || 'None'}</div>
            <div><strong>City:</strong> {volunteer.city || 'None'}</div>
            <div><strong>State:</strong> {volunteer.state || 'None'}</div>
            <div><strong>Pincode:</strong> {volunteer.pincode || 'None'}</div>
            <div><strong>Date of Birth:</strong> {volunteer.dateOfBirth || 'None'}</div>
            <div><strong>Gender:</strong> {volunteer.gender || 'None'}</div>
            <div><strong>Status:</strong> {volunteer.status || 'None'}</div>
            <div><strong>Form Data First Name:</strong> {formData.firstName || 'None'}</div>
            <div><strong>Form Data Last Name:</strong> {formData.lastName || 'None'}</div>
          </div>
          
          {/* Test API Button */}
          <div className="mt-4 pt-4 border-t border-yellow-200">
            <button
              onClick={async () => {
                try {
                  console.log('Testing API directly...');
                  const testResponse = await api.get(`/volunteers/test/${id}`);
                  console.log('Test API Response:', testResponse);
                  toast.success('Test API call successful! Check console.');
                } catch (error) {
                  console.error('Test API Error:', error);
                  toast.error('Test API call failed! Check console.');
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Test API Directly
            </button>
            <button
              onClick={async () => {
                try {
                  console.log('Testing regular API...');
                  const testResponse = await api.get(`/volunteers/${id}`);
                  console.log('Regular API Response:', testResponse);
                  toast.success('Regular API call successful! Check console.');
                } catch (error) {
                  console.error('Regular API Error:', error);
                  toast.error('Regular API call failed! Check console.');
                }
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm ml-2"
            >
              Test Regular API
            </button>
          </div>
        </div>

      <form onSubmit={handleSubmit} className="space-y-6">
                 {/* Profile Picture Section */}
         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
           <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Profile Picture</h3>
           
           <div className="flex items-center space-x-6">
             <div className="relative">
               <img
                 src={previewImage || '/images/r.png'}
                 alt="Profile"
                 className="w-24 h-24 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
                 onError={(e) => {
                   if (e.target) {
                     e.target.src = '/images/r.png';
                   }
                 }}
               />
               {profileImage && (
                 <button
                   type="button"
                   onClick={removeImage}
                   className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                 >
                   <X className="w-4 h-4" />
                 </button>
               )}
             </div>
             
             <div className="flex-1">
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 Upload New Picture
               </label>
               <div className="flex items-center space-x-3">
                 <input
                   type="file"
                   accept="image/*"
                   onChange={handleImageChange}
                   className="hidden"
                   id="profile-image"
                 />
                 <label
                   htmlFor="profile-image"
                   className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
                 >
                   <Upload className="w-4 h-4 mr-2" />
                   Choose Image
                 </label>
                 <span className="text-sm text-gray-500">
                   {profileImage ? profileImage.name : 'No file chosen'}
                 </span>
               </div>
               <p className="text-xs text-gray-500 mt-1">
                 Max size: 5MB. Supported formats: JPG, PNG, GIF
               </p>
                               {/* Debug info for profile picture */}
                <div className="text-xs text-gray-500 mt-2">
                  <strong>Raw Path:</strong> {volunteer.profilePicture || 'None'} | 
                  <strong>Display URL:</strong> {previewImage || 'None'}
                </div>
             </div>
           </div>
         </div>

        {/* Basic Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                         <div>
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 First Name *
               </label>
               <input
                 type="text"
                 name="firstName"
                 value={formData.firstName}
                 onChange={handleInputChange}
                 required
                 className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
               />
               <div className="text-xs text-gray-500 mt-1">
                 <strong>Loaded:</strong> {volunteer.firstName || 'None'} | <strong>Form:</strong> {formData.firstName || 'None'}
               </div>
             </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Last Name *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Phone *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date of Birth *
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Gender *
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Address Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Address *
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                State *
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pincode *
              </label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Emergency Contact</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contact Name
              </label>
              <input
                type="text"
                value={formData.emergencyContact.name}
                onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Relationship
              </label>
              <input
                type="text"
                value={formData.emergencyContact.relationship}
                onChange={(e) => handleEmergencyContactChange('relationship', e.target.value)}
                placeholder="e.g., Parent, Spouse"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contact Phone
              </label>
              <input
                type="tel"
                value={formData.emergencyContact.phone}
                onChange={(e) => handleEmergencyContactChange('phone', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Skills */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Skills</h3>
          
          <div className="space-y-3">
            {formData.skills.map((skill, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="text"
                  value={skill}
                  onChange={(e) => handleArrayFieldChange('skills', index, e.target.value)}
                  placeholder="Enter a skill"
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                {formData.skills.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeArrayFieldItem('skills', index)}
                    className="p-3 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => addArrayFieldItem('skills')}
              className="flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <User className="w-4 h-4 mr-2" />
              Add Skill
            </button>
          </div>
        </div>

        {/* Interests */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Interests</h3>
          
          <div className="space-y-3">
            {formData.interests.map((interest, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="text"
                  value={interest}
                  onChange={(e) => handleArrayFieldChange('interests', index, e.target.value)}
                  placeholder="Enter an interest"
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                {formData.interests.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeArrayFieldItem('interests', index)}
                    className="p-3 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
            
            <button
              type="button"
              onClick={() => addArrayFieldItem('interests')}
              className="flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <Star className="w-4 h-4 mr-2" />
              Add Interest
            </button>
          </div>
        </div>

        {/* Availability */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Availability</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.availability.weekdays}
                onChange={() => handleAvailabilityChange('weekdays')}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Weekdays</span>
            </label>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.availability.weekends}
                onChange={() => handleAvailabilityChange('weekends')}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Weekends</span>
            </label>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.availability.evenings}
                onChange={() => handleAvailabilityChange('evenings')}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Evenings</span>
            </label>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.availability.flexible}
                onChange={() => handleAvailabilityChange('flexible')}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Flexible</span>
            </label>
          </div>
        </div>

        {/* Experience & Motivation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Experience & Motivation</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Experience
              </label>
              <textarea
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                rows={3}
                placeholder="Describe your relevant experience..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Motivation
              </label>
              <textarea
                name="motivation"
                value={formData.motivation}
                onChange={handleInputChange}
                rows={3}
                placeholder="Why do you want to volunteer?"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Status</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="status"
                value="active"
                checked={formData.status === 'active'}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="status"
                value="pending"
                checked={formData.status === 'pending'}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Pending</span>
            </label>
            
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="status"
                value="inactive"
                checked={formData.status === 'inactive'}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Inactive</span>
            </label>
          </div>
        </div>

        {/* Current Stats Display */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Current Statistics</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{volunteer.totalHours || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Hours</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{volunteer.rating || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Rating</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {volunteer.projects && Array.isArray(volunteer.projects) ? volunteer.projects.length : 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Projects</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {volunteer.joinDate ? new Date(volunteer.joinDate).toLocaleDateString() : 'N/A'}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Join Date</div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/ngo/dashboard/volunteers/manage')}
            className="px-6 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditVolunteerForm;
